import parseISO from 'date-fns/parseISO'
import format from 'date-fns/format'
import numeral from 'numeral'
import _ from 'lodash'
import { isBrowser } from 'browser-or-node'
import { TOPID, GLOBALID, GLOBALTYPE } from '../runtimeConstants'
import { objectId } from '..'
import debug from '../debug'
const { buildLink, idToLink } = require('../linkIds')
const { date, math, finance, text, number, peek } = require('../runtimeExtensions')
const { globalTypes } = require('../runtimeExtensions-defs')
const { FLAG_EXTERNAL } = require('./defflags')
const { flagIsSet } = require('../flagutils')
const isEmail = require('isemail')
const { assembleText, compileText, parseText, Scope, IndirectVirtual, Engine: { compileExpr } } = require('yatte')
const USERDATA = TOPID
const GLOBALS = GLOBALID
const GLOBAL_TYPENAME = GLOBALTYPE

const MODE_REPLACE = 0 // normal mode -- entire objects and arrays are always replaced
const MODE_INIT = 1 // initialization mode -- when initializing the runtime
const MODE_OVERLAY = 2 // overlay mode -- defined properties overwrite; undefined do NOT (recursively)
const MODE_OVERLAY_CHILD = 4 // recursing from overlay mode

// /* global BigInt */

/**
 * An instance of ObjectContext will exist for every data object created or used by an app during assembly.
 * It wraps and holds a YObject and handles evaluation of the object's virtuals.
 */
class ObjectContext {
  constructor (name, typeName = '', parentContext = undefined, typeDict = undefined, sourceObj = undefined) {
    this.name = name
    this.mutable = false
    if (parentContext) {
      if (!(parentContext instanceof ObjectContext || parentContext instanceof ArrayContext)) {
        throw new Error('parentContext must be an instance of ObjectContext')
      }
      if (typeDict && parentContext.typeDefs) {
        if (typeDict !== parentContext.typeDefs) {
          throw new Error('When supplying a parentContext, do not supply a mismatched typeDict')
        }
      }
    } // else no parent context --> this is the global (top-level) context
    this.parentContext = parentContext
    if (!typeDict && (!parentContext || !parentContext.typeDefs)) {
      throw new Error('ObjectContext constructor called with neither a valid parentContext nor typeDict')
    }
    this.typeDefs = typeDict || parentContext.typeDefs
    if (typeName) {
      this.typeDef = this.typeDefs[typeName]
      if (!this.typeDef) {
        throw new Error(`ObjectContext constructor called with invalid type name '${typeName}'`)
      }
    } else if (this.parentContext && this.name) { // see if we can infer the type from the parent context
      const parent = this.parentContext
      const parentProps = parent.typeDef && (parent.typeDef.variables || parent.typeDef.properties)
      const thisDef = parentProps && parentProps.find(p => p.name === this.name)
      this.typeDef = thisDef && this.typeDefs[thisDef.typeName]
    }
    this.virtualDefs = this.getVirtualsFor(this.typeDef) || {}
    this.children = {} // holds an instance of ObjectContext or ArrayContext for this context's child contexts
    this.scope = null
    this.scopeGetter = () => this.scope
    if (name === GLOBALS) {
      this.dataManager = new DataManager(this)
    }
    if (sourceObj && (typeof sourceObj === 'object')) {
      this.initSource(sourceObj)
    } else {
      this.source = null
    }
  }

  get instanceId () {
    const pinst = this.parentContext && this.parentContext.instanceId
    return (pinst && pinst !== GLOBALS) ? (pinst + '.' + this.name) : this.name
  }

  get global () {
    if (!this.parentContext) {
      return this
    } else {
      return this.parentContext.global
    }
  }

  get keyName () {
    if (this.name === GLOBALS) return undefined
    if (this.parentContext && (this.parentContext instanceof ArrayContext)) {
      if (this.parentContext.keyName) return this.parentContext.keyName
    }
    if ((this.typeDef.keyType && this.typeDef.key)
      || this.typeDef.keyType === 'none'
      || this.typeDef.type === 'table'
      || (this.typeDef.type === 'object' && this.typeDef.name.startsWith('_options_')) // todo: really make it a table?
    ) {
      return this.typeDef.key
    }
    // else check for temporary "pushed" ObjectContext (a la "getPushedContext")
    // (situations where we are invoking evaluation on an object returned from a query, whether to evaluate
    // label text for it or to map it to something else)
    if (this.name === null && this.parentContext && this.parentContext.linkedKeyName) {
      return this.parentContext.linkedKeyName
    }
    // else it must just be the default key
    return 'id$'
  }

  getKeyPath (nestedOnly = false, forTypeDef = undefined) {
    let typeDef = forTypeDef || this.typeDef
    const typeDict = this.typeDefs
    let currKey = ((typeDef.keyType && typeDef.key) || typeDef.keyType === 'none')
      ? typeDef.key
      : 'id$'
    let path = nestedOnly ? '' : currKey
    if (currKey && currKey !== 'id$') {
      const isCurrKey = v => v.name === currKey
      let varDef = typeDef.variables.find(isCurrKey)
      while (varDef && varDef.type === 'selection') {
        typeDef = typeDict[varDef.typeName]
        currKey = varDef.optionKeyName
        varDef = typeDef.variables.find(isCurrKey)
        path = (path ? (path + '.') : '') + currKey
      }
    }
    return path
  }
  
  get keyType () {
    if (this.name === GLOBALS) return undefined
    if (this.parentContext && (this.parentContext instanceof ArrayContext)) {
      if (this.parentContext.keyType) return this.parentContext.keyType
    }
    if ((this.typeDef.keyType && this.typeDef.key) || this.typeDef.keyType === 'none') {
      return this.typeDef.keyType
    }
    // else
    return 'id'
  }

  get hasVirtualDefs () {
    return Object.keys(this.virtualDefs).length
  }

  _createObjectContext (varName, typeName, evalOnly = false) {
    if (!evalOnly && this._createObjectState) {
      return this._createObjectState(varName, typeName, evalOnly)
    }
    return new ObjectContext(varName, typeName, this)
  }

  _createArrayContext (varName, typeName, keyName, evalOnly = false) {
    if (!evalOnly && this._createArrayState) {
      return this._createArrayState(varName, typeName, keyName, evalOnly)
    }
    return new ArrayContext(varName, typeName, keyName, this)
  }

  _getObjectId () {
    if (this.name === TOPID && this.parentContext.recordId) {
      return this.parentContext.recordId
    } // else
    return objectId()
  }

  _getTypedObject (sourceObj, retainId$, forceNew = false) {
    return createTypedObject(sourceObj, this.keyName, this.hasVirtualDefs && this.virtualDefs, this.typeDef.variables,
      this.typeDefs, retainId$, forceNew, this.name === TOPID && this.parentContext.recordId)
  }

  getValue (varName) {
    const value = this.scope ? this.scope.getProperty(varName, this.scopeGetter) : null
    const yvalue = value && value.__value // if it's a Yatte proxy object, get the target object
    return yvalue || value
  }

  getChildScope (varName) {
    return this.scope && this.scope.getChildYObject(varName, this.scopeGetter)
  }

  initRecordProps (recordId, catalogName, links, tenancy) {
    if (recordId) {
      if (typeof recordId !== 'string') {
        recordId = recordId.toString()
      }
      if (this.recordId) { // SHOULD already be set to the right thing, so double-check that that's the case
        debug.assert(this.recordId === recordId, 'record ID mismatch (1)', true, false)
      } else {
        // if for some reason it's not yet set, set it now
        this.recordId = recordId
      }
    }
    if (catalogName) {
      this.catalogName = catalogName
    }
    if (!this.links) {
      this.links = {}
    }
    if (links) { // build link dictionary from link array
      for (const link of links) {
        this.links[ObjectContext.getLinkKey(link.appName, link.userEmail)] = link
      }
      this.linkCount0 = Object.keys(this.links).length
    }
    if (tenancy) {
      this.tenancy = tenancy
    }
    const thisContext = this
    this.scope.value['appLink'] = function (appName, userEmail, userName = undefined) {
      const linkId = thisContext.getExtAppLinkId(appName, userEmail, userName)
      return !linkId ? '[link not available]' : buildLink(thisContext.tenancy, linkId)
    }
  }

  getExtAppLinkId (appName, userEmail, userName = undefined) {
    const emailError = userEmail ? isEmail.validate(userEmail, { errorLevel: true }) : 1
    const recordId = this.recordId
    const catalogName = this.catalogName
    if (appName && userEmail && !emailError && catalogName && recordId) {
      const key = ObjectContext.getLinkKey(appName, userEmail)
      let linkObj = this.links[key]
      let modified = false
      if (linkObj) {
        if (userName && (linkObj.userName !== userName)) {
          linkObj = {
            ...linkObj,
            userName,
          }
          modified = true
        }
      } else {
        linkObj = {
          _id: objectId(),
          catalogName,
          recordId,
          appName,
          userEmail,
          userName,
        }
        modified = true
      }
      if (modified) {
        const newLinks = {
          ...this.links,
          [key]: linkObj,
        }
        this.links = newLinks
      }
      return idToLink(linkObj._id.toString())
    }
  }

  static getLinkKey (appName, userEmail) {
    return appName + ':' + userEmail
    // even though right now we only allow a single (external) user to be associated with a record at a time,
    // we still allow multiple links with different users because they can potentially change over time
  }

  initMainContext (sourceObj) {
    debug.assert(this.name === GLOBALS)
    // sourceObj is the raw data from the server. It consists of 2 parts:
    //  - user data: data produced and modified by users in response to questions
    //  - static data: transitory query results cached for implementation reasons
    // as part of initialization, MOVE static data OUT OF sourceObj INTO dataManager:
    sourceObj = this.dataManager.extractStaticData(sourceObj)
    // now what's left in sourceObj is just the user-modifiable (mutable) data, so initialize that:
    return this.getChildContext(TOPID, sourceObj)
  }

  /**
   * Gets an existing (or creates a new) child context object.
   *
   * New child context objects are normally created (recursively) when initSource() is called.
   * This method is used to create a new context object under 3 circumstances (used to be 2):
   * 1) when there is no existing data for a child, but an empty data context is called for at runtime.
   * 2) during initialization when values are initially being supplied for user data
   * 3) during data overlay, when an externally acquired value is being merged with user data at runtime
   * Otherwise, this simply fetches an existing child State object.
   *
   * @param {string} varName The variable name of the object-type variable that is a child of the current context.
   * @param {object} sourceObj The JS object (if any) from which a new object state should be initialized IF it does
   * not already exist.
   */
  getChildContext (varName, sourceObj = null, mode = MODE_INIT) {
    let childState = this.children[varName]
    if (!childState) { // no existing data -- create a new object context for it
      const varDef = (this.typeDef.variables || this.typeDef.properties).find(def => def.name === varName)
      if (varDef) {
        if (varDef.type === 'selection' && this.getSelectionContext) {
          childState = this.getSelectionContext(varName, sourceObj)
        } else if (IsObjectVar(varDef)) {
          childState = varDef.isList
            ? this._createArrayContext(varName, varDef.typeName, varDef.keyName)
            : this._createObjectContext(varName, varDef.typeName)
          childState.initSource(sourceObj, mode === MODE_OVERLAY ? MODE_OVERLAY_CHILD : mode)
          childState.initVirtuals()
          childState.initScope()
          if (sourceObj && mode !== MODE_OVERLAY_CHILD) {
            childState.initSelections()
          }
          this.children[varName] = childState
        }
      } else {
        childState = null
      }
    } else { // object context has already been initialized...
      if (sourceObj) { // must be during initialization; existing user data is being supplied
        childState.initSource(sourceObj, mode === MODE_OVERLAY ? MODE_OVERLAY_CHILD : mode)
        childState.initVirtuals()
        if (mode === MODE_OVERLAY && varName) {
          this.didOverlayUserData(varName, childState.source)
        }
        childState.initScope() // <-- problem: this discards childState.source in favor of this.getChildScope(varName)!
        // For this reason, I have changed things (I believe!) higher up the call stack, so we should not get
        // to this spot anymore in case of an overlay operation.  This needs testing though.
        if (mode !== MODE_OVERLAY && varName === USERDATA) {
          // this must come BEFORE the call to initSelections, because initSelections has to evaluate
          // all the option expressions for various selection variables, and those options expressions
          // may refer to _.  Hopefully the options expressions don't ALSO require that all the selection
          // variables need to have previously been initialized, or we have a race condition!
          // It must also come before initScope, at least in the case of MODE_OVERLAY!
          this.initUserData(childState.scope)
        }
        if (mode !== MODE_OVERLAY_CHILD) {
          childState.initSelections()
        }
        if (varName === USERDATA) {
          // call AGAIN now that the selection variables have been initialized, to ensure _ has the final version
          this.initUserData(childState.scope)
        }
      }
    }
    return childState
  }

  /**
   * Initializes the Source Object of this ObjectContext. The Source Object is a JavaScript object containing
   * "own" properties representing the data in this ObjectContext.  The object can also (optionally) have a prototype
   * that encapsulates common behaviors ("virtuals") for objects of a certain type.
   *
   * initSource is recursive, so a call at a high (or the highest) level in a hierarchy will incorporate calls to
   * initialize all child context objects.
   *
   * @param {object} sourceObj The object that incorporates all the raw data in this context object
   */
  initSource (sourceObj, mode = MODE_INIT, recurseProtect = {}) {
    // if source object has a custom key, create a "hybrid" object that can behave as either an object or its key
    // (right now this should never be the case for custom object types, only tables)
    const keyName = this.keyName || 'id$' // externally "unkeyed" types have keyName === undefined; id$ is 'internal' key
    if (sourceObj && sourceObj.__value) { // unwrap proxy to reach actual value
      sourceObj = sourceObj.__value
    }
    const usePrimitive = ( // the following should basically correspond to: is sourceObj a row in a TABLE?
      // because for rows in tables, we use wrapped primitive objects rather than regular objects
      // to facilitate the runtime using == to check for equality between a key value and a table row.
      keyName && keyName !== 'id$' && !this.typeDef.keyType && sourceObj && (keyName in sourceObj)
    )
    if (
      (usePrimitive && (
        (this.source && this.source.toString() === sourceObj[keyName])
        || (!this.source && isPrimitiveWrapper(sourceObj))
      ))
      || (this.source === sourceObj)
      || (mode !== MODE_OVERLAY_CHILD && this.scope && this.scope.value === sourceObj)
    ) {
      // nothing to do, except make sure initSource has indeed initialized this.source :-)
      if (!this.source) {
        this.source = sourceObj
      }
      return
    }
    if (sourceObj) {
      // we make a copy sourceObj (-> newObj) that holds a cleaned-up object value
      // on which the scope will later be based.
      const newObj = usePrimitive
        ? wrapPrimitive(sourceObj[keyName])
        : Object.create(
          this.hasVirtualDefs
            ? this.virtualDefs
            : Object.getPrototypeOf(sourceObj),
        )
      if (this.scope && (mode === MODE_OVERLAY || mode === MODE_OVERLAY_CHILD)) {
        const existingValue = this.scope.bareValue
        // for overlays, need to "reset" the scope to accommodate change tracking
        this.scope = null
        deleteMatchingProps(this.children, sourceObj)
        deleteMatchingProps(this.childrenSel, sourceObj)
        // since we are aiming to "overlay" sourceObj "on top of" existing properties,
        // do the same by adding INTO sourceObj whatever existing properties there are
        // that are not already a part of sourceObj: the result should be the same.
        CopyNewProperties(sourceObj, existingValue)
      }
      // when key is id$ and id$ does not already have a preset/specific value, the record ID will be used
      const record_id = (keyName === 'id$' && this.name === TOPID && this.parentContext.recordId) || undefined
      const varsInType = (this.typeDef.variables || this.typeDef.properties) // todo: remove the unnecessary one
      const funcsInType = this.typeDef.functions && this.typeDef.functions.filter(f => f.addon)
      // initialize value for each variable (key first!); recurse to create child contexts
      const hasKey = Boolean(sourceObj[keyName])
      const propKeys = hasKey
        ? [keyName, ...Object.keys(sourceObj).filter(k => k !== keyName)]
        : Object.keys(sourceObj)
      for (const propKey of propKeys) {
        const propVal = sourceObj[propKey]
        const varDef = varsInType.find(vdef => vdef.name === propKey)
        const funcDef = !varDef && funcsInType
          && typeof propVal === 'function' && funcsInType.find(fdef => fdef.name === propKey)
        let legacySelVal
        if (varDef && varDef.type === 'selection' && varDef.cacheOpts) {
          legacySelVal = sourceObj[propKey + '_$']
        }
        this._initSourceProp(newObj, propKey, varDef, funcDef, propVal, usePrimitive, keyName, record_id,
          legacySelVal, mode, recurseProtect)
      }
      // ensure the object that is being initialized has a unique id$ property to work correctly in the runtime!
      if (!('id$' in newObj)) {
        if (hasKey) { // id$ should have been set by _initSourceProp!
          throw new Error(`Badly initialized object '${sourceObj[keyName]}' missing id$ property`)
        } else {
          Object.defineProperty(newObj, 'id$', { value: objectId(), enumerable: true })
        }
      }
      this.source = newObj
    } else { // !sourceObj
      this.source = null
    }
  } // end method initSource

  _initSourceProp (newObj, propName, varDef, funcDef, propVal, isPrimitive, keyName,
    record_id, selObjVal, mode, recurseProtect = {}
  ) {
    if (varDef) {
      // the property is a variable defined in the runtime type, ergo it is potentially relevant
      let isCacheableSelection = (varDef.type === 'selection' && varDef.cacheOpts)
      if (isCacheableSelection) {
        // must handle these situations/contexts:
        //   (A) doing a from-scratch initialization of an answer set, either on the server or the client
        //       --> val may be a bare key value (integer or string), or an array of such key values
        //   (B) selecting a new answer (data mutation, only on the client)
        //       --> val could be a full object, or an array of such objects
        //   (C) getting a temporary pushed context, on the client, for purposes of evaluating an option prompt for a selection variable
        //       --> val is a full object, or an array of such objects
        recurseProtect = { ...recurseProtect }
        if (varDef.isList) {
          propVal = GetCachedOrFallbackArray(propVal, selObjVal, this.getDataMgr(), varDef, false, recurseProtect)  
        } else {
          propVal = GetCachedOrFallbackObj(propVal, selObjVal, this.getDataMgr(), varDef, false, recurseProtect)
        }
        if (typeof propVal !== 'object') {
          const msg = `Cannot initialize object context based on key value '${
            propVal}': No corresponding object has been cached.`
          console.error(msg)
          // throw new Error(msg)
        }
        // make sure we are ready for selection child contexts
        if (this.mutateVarData && !this.childrenSel) {
          this.childrenSel = {}
        }
      }
      let coercedVal = CoerceValue(propVal, varDef, true)
      if (IsObjectVar(varDef) && !varDef.typeName.startsWith('__') // it's an object or selection variable
        && typeof coercedVal === 'object' // it has an object-type value
      ) {
        const childrenObj = (isCacheableSelection && this.childrenSel) ? this.childrenSel : this.children
        let childContext = childrenObj[propName]
        if (!childContext) {
          childContext = varDef.isList
            ? this._createArrayContext(propName, varDef.typeName, varDef.keyName)
            : this._createObjectContext(propName, varDef.typeName)
        }
        childContext.initSource(coercedVal, mode, recurseProtect)
        if (!childrenObj[propName]) {
          childrenObj[propName] = childContext
        }
        // ensure the child context's source, which has now been initialized, gets captured as the source object!
        coercedVal = childContext.source
      }
      this._assignObjectProperty(newObj, propName, varDef, coercedVal, isPrimitive, keyName, record_id)
      return
    } // else
    if (funcDef) {
      // it's a function -- at this point, on the global object -- so save it (there's probably a cleaner way)
      newObj[propName] = propVal
      return
    } // else
    if (propName === '_link') { // special handling for linked key values
      newObj[propName] = propVal // always retain it
      if (this.linkedKeyVal) {
        debug.assert(this.linkedKeyVal === propVal)
      } else { // AND notify the data state that it's linked, I guess? maybe?
        this.linkedKeyVal = propVal
        this.linkedKeyName = '_N_A_'
      }
      return
    } // else
    if (propName === 'id$') { // since no varDef was defined, must be on a non-native model (?)
      // carry it across unless a native ID is already defined
      if (!('id$' in newObj)) {
        Object.defineProperty(newObj, 'id$', { value: propVal.toString(), enumerable: true })
      }
      return
    } // else
    // property is not recognized in app definition, so "box" it for later
    if (this.typeDef.type !== 'table' && !(propName.endsWith('_$'))) {
      if (this.box) { // this.box will only be present for instances of ObjectState, not ObjectContext!
        // box up properties of inputObj that AREN'T in typeName so they don't clutter but can still roundtrip
        this.box[propName] = propVal
      }
    }
  }

  _assignObjectProperty (newObj, propName, varDef, propVal, isPrimitive, keyName, record_id) {
    if (isPrimitive) { // this object context is for a table row,
      // where properties are read-only and attached to a wrapped primitive
      Object.defineProperty(newObj, propName, { value: propVal })
      if (propName === keyName) {
        Object.defineProperty(newObj, 'id$', { value: propVal.toString() })
      }
      return
    } // else
    // this object context is for a regular object, so assign regular properties
    if (varDef.type === 'selection' && isPrimitiveWrapper(propVal)) {
      newObj[propName] = propVal.valueOf()
    } else if (propName === keyName) {
      // special handling for object key values
      this._assignObjectKey(newObj, varDef, propVal, keyName, record_id)
    } else if (propName !== 'id$') { // if keyName is 'id$', it would have been handled above; otherwise ignore 'id$'
      // normal case for most properties: simple assignment
      newObj[propName] = propVal
    }
  }

  _assignObjectKey (newObj, varDef, propVal, keyName, record_id) {
    if (varDef.type !== 'selection') {
      // normal case for key values of type text, number, id -- i.e., NOT nested selections
      if (keyName === 'id$') {
        Object.defineProperty(newObj, 'id$', { value: propVal || record_id, enumerable: true })
      } else {
        Object.defineProperty(newObj, keyName, { value: propVal, enumerable: true })
        if (!('id$' in newObj)) {
          Object.defineProperty(newObj, 'id$', {
            value: (propVal && propVal.toString()) || record_id,
            enumerable: true,
          })
        }
      }
      return
    } // else
    // varDef.type === 'selection'
    // the key for a selection variable CAN be another (nested) selection variable,
    // in which case, we need to keep track of the nested key info:
    const nestedKeyName = varDef.optionKeyName
    const nestedKeyValue = propVal && (propVal.__keyVal || propVal[nestedKeyName])
    if (nestedKeyValue || record_id) {
      Object.defineProperty(newObj, '__key', { value: nestedKeyName }) // not enumerable
      Object.defineProperty(newObj, '__keyVal', { value: nestedKeyValue }) // not enumerable
      newObj['__keyObj'] = propVal // warning: enumerable! Beware when enumerating props in unwrap(), cleanObject(), etc
      Object.defineProperty(newObj, keyName, {
        enumerable: true, // deliberately enumerable -- want to see this one :-)
        get () {
          if (this.__keyObj) {
            debug.assert(this.__keyVal === this.__keyObj[this.__key])
          }
          return this.__keyObj
        },
        set (newValue) {
          if (newValue) {
            debug.assert(this.__keyVal === newValue[this.__key])
          }
          this.__keyObj = newValue
        },
      })
    }
    if (!('id$' in newObj)) {
      const value = (nestedKeyValue && nestedKeyValue.toString()) || record_id || objectId()
      Object.defineProperty(newObj, 'id$', { value, enumerable: true })
    }
  }

  // TODO: look at this obscure case, which once upon a time was a bug/problem, but now things have been reorganized
  //       and it's not known if it still is a problem or not:
  //
  // when sourceObj represents a knackly custom 'matter' type selected FROM a (mapped) Clio Matter,
  // and sel variable is a knackly custom 'contact' selection, whose option source is a Knackly 'contact'
  // query, but whose actual value cam from a nested mapping from Clio Contact to knackly 'contact'.
  // In this situation,
  // varName === 'client',
  // optionKeyName === 'id$' (now, used to be === '_id') because that is expected to be the ID of an item
  //                                                                                 in a Knackly catalog
  // newVal[optionKeyName] (used to ===) undefined because the contact object is (not yet) in Knackly
  // val === '1322178484:ToContact' because that is what we got when mapping the Clio contact to ours

  initVirtuals () {
    // abstract

    // initVirtuals is unnecessary and doesn't do anything on the server / when dealing with
    // instances of ObjectContext. It is needed on the client when dealing with (mutable) ObjectState,
    // where it enables some optimization of frequently-used expressions/templates that power app UIs in the browser.
  }

  /**
   * Converts the Source Object (previously set via initSource) into a Scope/YObject, so it is ready
   * to be used in Yatte evaluation.  Also ensures the YObject is created with the necessary "virtuals".
   * Recursive.
   *
   * Note that when recursing, initScope may not use its own Source object as the basis for its scope;
   * it must use the same Scope object as is embedded in the parent context's scope.
   */
  initScope () {
    if (this.parentContext instanceof ArrayContext) { // this is an item in a list
      const index = this.name
      this.scope = Scope.pushListItem(index, this.parentContext.scopeGetter)
    } else { // if (!(this.parentContext instanceof ArrayContext)) {
      // at this point, when this was originally written, we did something that later proved problematic:
      // we just set THIS context's scope to the corresponding child of the PARENT context's scope.
      // This works assuming that scopes are being initialized from the top down: the parent's scope
      // will always be initialized prior to the child's scope.
      // But it BREAKS when we're being called as part of an Overlay operation, when the entire nested
      // data context is already initialized with one set of values, and we suddenly need to refresh it with
      // a potentially very different value.  TO FIX THIS, I changed things higher up the call stack so
      // overlay operations should no longer come this way!
      const pScope = this.parentContext && this.parentContext.getChildScope(this.name)
      if (pScope && pScope.value && Object.keys(pScope.value).length > 0) {
        // comment out the following check, since initSource can now be used to overlay values on existing scope!
        // if (this.source && (this.source !== pScope.value)) throw new Error('Things are not as one wishes they were')
        // i.e., we're trying to initialize an already-initialized, non-empty scope to something else... why?

        // TODO: pScope doesn't seem like it would have the right virtuals defined, does it?
        // And does it have the appropriate parent scope getter? -- no, it doesn't
        // should we, instead, create the correct YObject here, and graft it into the parent,
        // rather than the other way around?
        if (this.parentContext && typeof pScope.parent !== 'function') {
          pScope.parent = this.parentContext.scopeGetter // fix/dynamicize parent
        }
        this.scope = pScope
      } else {
        this.scope = Scope.pushObject(this._getTypedObject(this.source, true),
          this.parentContext && this.parentContext.scopeGetter)
      }
    }
    this.source = undefined // remove source now that scope is initialized (?)
    // force initialization of child contexts
    const children = Object.values(this.children)
    if (this.childrenSel) {
      children.push(...Object.values(this.childrenSel))
    }
    children.forEach(child => child.initScope())
  }

  // return true if any selections were not able to be initialized -- may be called again in that case?
  initSelections () {
    const notResolved = []
    const obj = this.scope && this.scope.value // we are attempting to fix up selections WITHIN the YObject
    if (!obj) {
      debug.log('Non-critical? : initSelections called on undefined or null object')
      return
    }
    const typedef = this.typeDef
    const varsCollection = typedef.variables || typedef.properties // temporarily, during transition
    for (const varDef of varsCollection.filter(v => v.type === 'selection' || v.type === 'object')) {
      const varName = varDef.name
      // If we needed a trigger to initiate async fetching of linked child records, it could maybe go here...?
      if (objHasProp(obj, varName)) {
        if (IsObjectVar(varDef) && ((varName in this.children) || (this.childrenSel && (varName in this.childrenSel)))
        ) {
          // recurse to child objects to initialize their selection variables too
          if (varName in this.children) {
            this.children[varName].initSelections()
          } else {
            this.childrenSel[varName].initSelections()
          }
        } else if (varDef.type === 'object') {
          if (!varDef.typeName || !varDef.typeName.startsWith('__')) {
            console.log(`Error: '${varName}' children collection not initialized prior to calling initSelections`)
          }
        } else if (varDef.type === 'selection') {
          // initialize a selection variable on this object
          const keyValue = obj[varName]
          let optionArray
          if (varDef.cacheOpts) {
            const cachedVal = this.getDataMgr().getCachedValue(varDef.typeName, keyValue, true, true)
            if (cachedVal && typeof cachedVal === 'object') {
              optionArray = Array.isArray(cachedVal) ? cachedVal : [cachedVal]
            }
            if (!optionArray) {
              console.log('error: expected option to be cached, but it was not; selected option may be lost.')
            }
          } else { // !varDef.cacheOpts
            optionArray = this._fetchOptions(varDef)
            if (!optionArray) {
              console.log('warning: option array undefined; selected option may be lost.')
            }
          }
          // above may return an array of simple objects OR an array of proxies!
          // (objects with their own virtuals and evaluation context)
          if (optionArray) {
            if (optionArray.length > 0 && typeof optionArray[0] === 'object') {
              if (!varDef.isList) {
                obj[varName] = LookupOption(optionArray, CoerceScalar(keyValue, varDef, true))
              } else {
                obj[varName] = CoerceArray(keyValue, varDef, true).map(elemKey => LookupOption(optionArray, elemKey))
              }
            } else {
              notResolved.push(varName)
            }
          }
        }
      }
    }
  }

  initQueries (needDataAsyncFunc = undefined) {
    if (this.name !== GLOBALS) {
      throw Error('Only call initQueries on the top (global) data context.')
    }
    if (needDataAsyncFunc) {
      // set up async callback to fetch queries when the runtime needs them
      this.dataManager.needDataAsyncFunc = needDataAsyncFunc
    }
    // set up query delegates: cached "answers" that invoke delegates to get the actual data
    for (const [queryKey, queryDelegate] of Object.entries(this.dataManager.delegates)) {
      this.source[queryKey] = queryDelegate
    }
  }

  initUserData (dataScope) {
    // called only once, on the top-level (global) data context, to finalize the initialization of the user data context
    delete this.scope.items[USERDATA]
    this.scope.value[USERDATA] = dataScope.value
    this.scope.items[USERDATA] = dataScope
  }

  didOverlayUserData (name, data) {
    // called during the updating of user data through an overlay operation...?
    delete this.scope.items[name]
    this.scope.value[name] = data
  }

  static clearCaches () {
    compileExpr.cache = {}
    compileText.cache.clear()
    parseText.cache = {}
    // also clear table cache to avoid stale table definitions
    // RealizeTable.cache = {}
    // and virtuals cache
    // GetVirtuals.cache = {}
    // also clear options cache to avoid stale selection variable options
    // GetOptions.cache = {}
  }

  getVirtualsFor (typeDef) {
    let virtuals
    if (typeDef) {
      virtuals = typeDef.virtuals
      if (!virtuals &&
        ((typeDef.formulas && typeDef.formulas.length) || (typeDef.templates && typeDef.templates.length))
      ) {
        // store this type's virtuals on the typeDef so they don't need to be created again
        typeDef.virtuals = virtuals = {}
        if (typeDef.formulas) {
          typeDef.formulas.forEach(formulaDef => {
            let compiledFormula = compileExpr(getFormulaExpression(formulaDef))
            compiledFormula.lbl = typeDef.name + '::' + formulaDef.name
            if (formulaDef.type === 'object' && formulaDef.typeName) {
              // wrap single-object formulas in code to cast them to the correct type if necessary!
              const formulaType = this.typeDefs[formulaDef.typeName]
              const formulaProto = this.getVirtualsFor(formulaType)
              if (formulaProto) {
                compiledFormula = (new CoercedExpression(compiledFormula, formulaDef.isList, formulaProto)).get
              }
            }
            virtuals[formulaDef.name] = compiledFormula
          })
        }
        if (typeDef.templates) {
          typeDef.templates.forEach(templateDef => {
            if (isBrowser) { // i.e. assembling in the browser
              const compiledTemplate = compileText(templateDef.text || '""')
              compiledTemplate.lbl = typeDef.name + '::' + templateDef.name
              virtuals[templateDef.name] = compiledTemplate
            } else { // assembling on the server in node.js
              if ( // specific template types that require async handling or separate-file output
                templateDef.type === 'docx'
                || (templateDef.type === 'text' && templateDef.contentType === 'markdown')
                || templateDef.type === 'file'
                || templateDef.type === 'pdff'
              ) {
                virtuals[templateDef.name] = (new IndirectTemplate(templateDef, typeDef)).get
              } else { // all other cases
                virtuals[templateDef.name] = compileText(templateDef.text || '""')
              }
            }
          })
        }
      }
    }
    return virtuals
  }

  evaluateExpression (expr) {
    return this.scope.evaluate(compileExpr(expr))
  }

  // async evaluateAsync (expr) {
  //   const dataMgr = this.getDataMgr()
  //   let v = this.evaluateExpression(expr)
  //   if (dataMgr.newData) {
  //     await dataMgr.dataPromise
  //     // and must re-evaluate expr so it gets correct value!
  //     v = this.evaluateExpression(expr)
  //   }
  //   return v
  // }

  _fetchOptions (varDef) {
    let options = this.evaluateExpression(varDef.options)
    const optionModel = this.typeDefs[varDef.typeName]
    const key = varDef.optionKeyName
    const usePrimitive = key && key !== 'id$' && !optionModel.keyType
    if (usePrimitive) {
      // ensure options get converted to a "hybrid" array of String objects (with add'l properties) rather than
      // a regular object array.
      if (options && options.length > 0) {
        const array = options.__value || options
        if (!isPrimitiveWrapper(array[0])) {
          options = array.map(option => wrapPrimitive(option[key], option, key))
        }
      }
    }
    return options
  }

  _fetchSummary () {
    return assembleText(this.typeDef.summary || '', this.scope)
  }

  /**
   * Pushes obj onto the context stack represented by this, and returns the pushed context suitable for evaluation
   * of templates or formulas.
   *
   * @param {*} obj a pojo (Plain Old JavaScript Object) to be wrapped in a new context object
   */
  getPushedContext (obj, typeName) {
    const ctx = this._createObjectContext(null, typeName, true)
    ctx.initSource(obj)
    ctx.initVirtuals()
    ctx.initScope()
    ctx.initSelections()
    return ctx
  }

  getVarOptions (name) {
    const typedef = this.typeDef
    const varsCollection = typedef.variables || typedef.properties // temporarily, during transition
    const selDef = varsCollection.find(v => v.name === name)
    let opts = this._fetchOptions(selDef)
    if (opts && opts.length === 0 && selDef.cacheOpts) { // no options returned; check if there's a cached selection!
      const cachedSel = this.getChildContext(name)
      if (cachedSel) {
        opts = [cachedSel.scope.proxy] // single item array
      }
    }
    return opts
  }

  getSummary () {
    return this._fetchSummary().toString()
  }

  getDataMgr () {
    if (this.name === GLOBALS) return this.dataManager
    if (this.parentContext instanceof ArrayContext) return this.parentContext.parentContext.getDataMgr()
    return this.parentContext.getDataMgr()
  }

  /**
   * Returns a POJO (Plain Old Javascript Object) containing all the data encapsulated by this ObjectState,
   * in the format meant for sending to the server, without any of the transitory stuff.
   * Includes "static" data that resulted from dynamic queries.
   * Also includes "boxed" data that came from the server but was not relevant to the current app.
   */
  cleanObject () {
    if (!this.scope) debugger
    if (!this.scope.value) return this.scope.value
    const pojo = {}
    const typedef = this.typeDef
    const varsCollection = typedef.variables || typedef.properties
    for (const propName of Object.keys(this.scope.value)) {
      const propVal = this.scope.value[propName]
      if (propVal !== null && propVal !== undefined) {
        if (propName === '_link') {
          // special key: indicates a linked key for the source of this object; always retain
          pojo[propName] = propVal
        } else if (
          propName === 'id$' && (
            typedef.keyType === 'none'
            || (typedef.name.startsWith('clio:') && typedef.name.endsWith('Custom'))
          )
        ) {
          debug.assert(typedef.keyType === 'none', 'keyType should be "none" but it is not!')
          // never output key property for explicitly keyless objects
          // TODO: once keyType==='none' works as expected, remove clio-specific stuff
        } else {
          const varDef = varsCollection.find(p => p.name === propName)
          if (varDef) {
            pojo[propName] = varDef.isList
              ? this._cleanArray(propVal, propName, varDef)
              : this._cleanScalarValue(propVal, propName, varDef)
            if (varDef.cacheOpts) {
              // if (varDef.isList) {
              //   pojo[key + '_$'] = val.filter(item => item && (typeof item === 'object')).map(item => item.valueOf())
              // } else {
              //   pojo[key + '_$'] = val.valueOf() // research: can there be anything nested in here to worry about?
              // }
              // Instead, verify that the objects underlying the selection variable are already in __cache__!!!
              const dm = this.getDataMgr()
              if (varDef.isList) {
                const keyArray = pojo[propName]
                const cachedArray = keyArray.map(k => dm.getCachedValue(varDef.typeName, k, false, true))
                debug.assert(cachedArray
                  && cachedArray.length === keyArray.length
                  && cachedArray.every((cachedItem, index) => (
                    cachedItem && cachedItem[varDef.optionKeyName] === keyArray[index]
                  )),
                  'Cached array mismatch'
                )
                // const tempVal = val.filter(item => item && (typeof item === 'object')).map(item => item.valueOf())
                // debug.assert(deepEqual(tempVal, cachedArray), 'Cached array mismatch')
              } else {
                var keyVal = pojo[propName]
                const cachedValue = dm.getCachedValue(varDef.typeName, keyVal, false, true)
                debug.assert(cachedValue && cachedValue[varDef.optionKeyName] === keyVal, 'Cached object mismatch')
                // debug.assert(deepEqual(val.valueOf(), cachedValue), 'Cached object mismatch')
              }
            }
          } else {
            // no variable definition in this app... where did it come from?
            // newObj[key] = val
          }
        }
      }
    }
    // Also pass back values that came in originally but were not recognized or used by the current app.
    // NOTE: the server should probably ignore these values and pay attention only to stuff the current app cares about,
    // but we pass surplus values back just in case, since it was probably the server who gave them to us
    // in the first place.
    for (const key in this.box) {
      if (!objHasProp(pojo, key)) { // this should never be true, unless something weird is happening
        pojo[key] = this.box[key]
      }
    }
    return pojo
  }

  _cleanScalarValue (value, varName, varDef) {
    if (Array.isArray(value)) {
      throw new Error('Array found where single value expected')
    }
    switch (varDef.type) {
      case 'selection':
        if (typeof value === 'object' && value !== null) {
          if (isPrimitiveWrapper(value)) {
            return value.valueOf()
          } else if (value instanceof Date) {
            // not possible yet, but in the future if a table can have a date column, and the date column can be key...
            return format(value, 'yyyy-MM-dd')
          } else if (varDef.optionKeyName in value) {
            let keyValue = value[varDef.optionKeyName]
            if (typeof keyValue === 'object' && keyValue) { // selection variable's key is itself an object!
              const keyValType = this.typeDefs[varDef.typeName]
              const keyVarDef = keyValType && keyValType.variables.find(v => v.name === varDef.optionKeyName)
              if (keyVarDef && keyVarDef.type === 'selection') { // simplify to key of THAT selection variable
                keyValue = this._cleanScalarValue(keyValue, keyVarDef.name, keyVarDef)
              }
            }
            return keyValue
          } else {
            return value.id$
          }
        } else {
          return value
        }
      case 'text':
        return value && String(value)
      case 'number':
        return numeral(value).value()
      // case 'bigint':
      //   return BigInt(value)
      case 'date':
        if (value && typeof value === 'string') {
          value = parseISO(value)
        }
        return value && format(value, 'yyyy-MM-dd')
      case 'object': {
        const childContext = this.children[varName]
        return childContext && childContext.cleanObject()
      }
      default:
        return value
    }
  }

  _cleanArray (value, varName, varDef) {
    if (value && !Array.isArray(value)) {
      throw new Error('Single value found where array expected')
    }
    switch (varDef.type) {
      case 'object': {
        const childContext = this.children[varName]
        if (childContext && childContext.elements.length !== value.length) {
          throw new Error('ArrayState in a bad state (not initialized correctly)')
        }
        return childContext && childContext.cleanArray()
      }
      default:
        return value.map(itemVal => this._cleanScalarValue(itemVal, '', varDef))
    }
  }

  static initializeRuntime (appDef) {
    const {
      typeDict,
      constDict,
    } = InitAppState(appDef)
    const globalContext = new ObjectContext(GLOBALS, GLOBAL_TYPENAME, null, typeDict, constDict)
    globalContext.appKey = appDef.ksvid
    globalContext.initQueries()
    globalContext.initVirtuals() // must be called because it's recursive
    globalContext.initScope()
    globalContext.initSelections() // needed if global objects have selection variables!
    return globalContext
  }
}

class ArrayContext {
  constructor (
    name, typeName, keyName = undefined, parentContext = undefined, typeDict = undefined, sourceArray = undefined,
  ) {
    this.name = name
    this.mutable = false
    if (parentContext) {
      if (!(parentContext instanceof ObjectContext)) {
        throw new Error('parentContext must be an instance of ObjectContext')
      }
      if (typeDict && parentContext.typeDefs) {
        if (typeDict !== parentContext.typeDefs) {
          throw new Error('When supplying a parentContext, do not supply a mismatched typeDict')
        }
      }
    }
    this.parentContext = parentContext
    if (!typeDict && (!parentContext || !parentContext.typeDefs)) {
      throw new Error('ArrayContext constructor called with neither a valid parentContext nor typeDict')
    }
    this.typeDefs = typeDict || parentContext.typeDefs
    if (!typeName) {
      throw new Error('No typeName supplied to ArrayContext')
    }
    this.typeDef = this.typeDefs[typeName]
    if (!this.typeDef) {
      throw new Error(`ObjectContext constructor called with invalid type name '${typeName}'`)
    }
    this.virtualDefs = (this.parentContext && this.parentContext.getVirtualsFor(this.typeDef)) || {}
    this.scope = null
    this.scopeGetter = () => this.scope
    this.keyName = keyName || (this.typeDef.keyType && this.typeDef.key)
    if (sourceArray) {
      this.initSource(sourceArray) // also initializes this.elements
    } else {
      this.source = null
      // build out child elements if we can determine how many there should be
      let elemCount = 0
      if (parentContext) {
        const probableFutureSource = (parentContext.source && parentContext.source[name])
          || parentContext.getChildScope(name)
        if (probableFutureSource) {
          if (probableFutureSource.length) { // is this ever true? I'm leaving it in because that's what used to happen
            elemCount = probableFutureSource.length
          } else if (probableFutureSource.items) { // seems like maybe the right thing
            elemCount = probableFutureSource.items.length
          }
        }
      }
      const evalOnly = !this.getStatus
      const thisContext = this
      this.elements = Array.from(Array(elemCount).keys()).map(
        (undef, index) => thisContext._createObjectContext(index, typeName, evalOnly)
      )
    }
  }

  get isList () {
    return true
  }

  get instanceId () {
    const pinst = this.parentContext && this.parentContext.instanceId
    return (pinst && pinst !== GLOBALS) ? (pinst + '.' + this.name) : this.name
  }

  get global () {
    return this.parentContext.global
  }

  get hasVirtualDefs () {
    return Object.keys(this.virtualDefs).length
  }

  _createObjectContext (varName, typeName, evalOnly = false) {
    if (!evalOnly && this._createObjectState) {
      return this._createObjectState(varName, typeName, evalOnly)
    }
    return new ObjectContext(varName, typeName, this)
  }

  getKeyPath (nestedOnly = false, forTypeDef = undefined) {
    return this.parentContext.getKeyPath(nestedOnly, forTypeDef || this.typeDef)
  }

  getValue (index) {
    const value = this.scope && this.scope.getListValue(index)
    const yvalue = value && value.__value // if it's a Yatte proxy object, get the target object
    return yvalue || value
  }

  getChildScope (index) {
    return this.scope && this.scope.getListItem(index)
  }

  getChildContext (index) {
    let childState = this.elements[index]
    if (!childState) {
      // const childScope = this.scope && this.scope.items[index] // unnecessary -- initScope gets it right already
      // Also, do we need a way to distinguish, here, between child selection contexts and child object contexts,
      // as we do in ObjectState's getChildContext ??
      // const varDef = (this.typeDef.variables || this.typeDef.properties)
      //   .find(def => def.name === this.parentContext.name)
      // if (varDef && IsObjectVar(varDef)) {
      childState = this._createObjectContext(index, this.typeDef.name)
      childState.initVirtuals()
      childState.initScope()
      this.elements[index] = childState
      // } else if (varDef && varDef.type === 'selection' && this.getSelectionContext) {
      //   childState = this.getSelectionContext(index)
      // }
    }
    return childState
  }

  _getTypedObject (sourceObj, retainId$, forceNew = false) {
    return createTypedObject(sourceObj, this.keyName, this.hasVirtualDefs && this.virtualDefs, this.typeDef.variables,
      this.typeDefs, retainId$, forceNew)
  }

  /**
   * Initializes the Source Array of this ArrayContext. The Source Array is a JavaScript array containing
   * items representing the data in this ObjectContext.  Items in the array can also (optionally) have a prototype
   * that encapsulates common behaviors ("virtuals") for objects of a certain type.
   *
   * initSource is recursive, so a call at a high (or the highest) level in a hierarchy will incorporate calls to
   * initialize all child state objects.
   *
   * @param {object[]} sourceArray The array of objects incorporating all the raw data in this state object
   */
  initSource (sourceArray, mode = MODE_INIT, recurseProtect = {}) {
    const keyName = this.keyName
    const usePrimitive = keyName && keyName !== 'id$' && !this.typeDef.keyType
    if (!usePrimitive && (this.source === sourceArray || (this.scope && this.scope.value === sourceArray))) {
      // nothing to do
      return
    }
    if (sourceArray) {
      // ensure sourceArray is an array and not a single item
      if (!Array.isArray(sourceArray)) {
        sourceArray = [sourceArray]
      }
      // ensure the necessary child contexts exist and are each initialized
      if (!this.elements) {
        this.elements = []
      } else if (this.elements.length > sourceArray.length) {
        this.elements.splice(sourceArray.length, (this.elements.length - sourceArray.length))
      }
      sourceArray.forEach(
        (element, index) => {
          let childState = this.elements[index]
          if (childState) {
            childState.initSource(element, mode, recurseProtect)
          } else {
            const evalOnly = !this.mutateVarData // this.constructor.name.endsWith('Context')
            childState = this._createObjectContext(index, this.typeDef.name, evalOnly)
            if (element && typeof element !== 'object') {
              // the selection variable is referring to an object that apparently does not exist or is not available,
              // so... punt?
              element = null
            }
            childState.initSource(element, mode, recurseProtect)
            // don't call childState.initVirtuals(), because we need to initSource recursively and THEN initVirtuals
            this.elements[index] = childState
          }
        },
      )
      // if (usePrimitive) { // commented out, because childState.initSource above means element source objects
      // are the correct type, whereas in sourceArray they may not be!
      this.source = this.elements.map(element => element.source)
      // } else {
      //   this.source = sourceArray
      // }
    } else {
      this.source = []
    }
  }

  initVirtuals () {
    // recurse to elements
    if (this.typeDef.type !== 'table') {
      this.elements.forEach(child => child.initVirtuals())
    }
  }

  /**
   * Converts the Source Array (previously set via initSource) into a Scope/YList, so it is prepared
   * to be used in Yatte evaluation.  Also ensures the YListItems are created with the necessary "virtuals".
   * Recursive.
   */
  initScope () {
    const pScope = this.parentContext && this.parentContext.getChildScope(this.name)
    if (pScope) {
      if (this.parentContext && typeof pScope.parent !== 'function') {
        pScope.parent = this.parentContext.scopeGetter // fix/dynamicize parent
      }
      this.scope = pScope
    } else {
      const prototype = this.hasVirtualDefs ? this.virtualDefs : Object.prototype
      if (this.source) {
        this.scope = Scope.pushList(
          (this.source.length > 0 && this.source[0]
            && Object.getPrototypeOf(this.source[0]) === prototype)
            ? this.source
            : this.source.map(element => Object.assign(Object.create(prototype), element)),
          this.parentContext && this.parentContext.scopeGetter)
      } else if (
        this.scope && this.scope.value && this.scope.items
        && this.scope.items.length === this.scope.value.length
        && this.scope.value.every((v, index) => v === this.scope.items[index].value)
      ) {
        // do nothing -- seems like scope is already as initialized as it needs to be?
      } else {
        this.scope = Scope.pushList([], this.parentContext && this.parentContext.scopeGetter)
        // question: can we find ourselves here during an overlay operation?  I don't think so, but what if...?
      }
    }
    this.source = undefined // remove source now that scope is initialized
    // force initialization of child states
    this.elements.forEach((element, index) => {
      element.initScope()
    })
  }

  initSelections () {
    // recurse to elements
    this.elements.forEach(child => child.initSelections())
  }

  cleanArray () {
    return this.elements.map(itemState => itemState.cleanObject())
  }
}

class CoercedExpression {
  constructor (expr, isList = false, typePrototype = undefined) {
    if (expr) {
      if (typeof expr === 'function') {
        this.recalculate = expr
        if (expr.lbl) {
          this.lbl = expr.lbl
        }
      } else {
        this.recalculate = compileExpr(expr)
      }
    } else {
      this.recalculate = (s, l) => isList ? [] : ''
      this.recalculate.ast = true
    }
    if (typePrototype) {
      this.forceProto = typePrototype
    }
    this.isList = isList
    this.get = this.get.bind(this)
    this.get.ast = this.recalculate.ast
  }

  get (scope) {
    let stack
    if (scope) {
      stack = scope.__yobj || ((scope instanceof Scope) ? scope : Scope.pushObject(scope))
    }
    let val = stack && stack.evaluate(this.recalculate)
    if (val && this.forceProto) {
      if (!this.isList) {
        if (Object.getPrototypeOf(val) === Object.prototype) {
          const yobj = Scope.pushObject(
            Object.assign(Object.create(this.forceProto), val),
            scope._parent.__yobj,
          )
          val = yobj.proxy
        }
      } else { // this.isList
        if (Array.isArray(val) && val.length > 0
          && val.some(element => element && Object.getPrototypeOf(element.__value || element) === Object.prototype)
        ) {
          const typedList = val.map(element => {
            if (element && Object.getPrototypeOf(element.__value || element) === Object.prototype) {
              element = Object.assign(Object.create(this.forceProto), element.__value || element)
            }
            return element
          })
          const ylist = Scope.pushList(typedList, scope.__yobj)
          val = ylist.scopeProxy
        }
      }
    }
    return val
  }
}

CoercedExpression.prototype.get.ast = true

function ConsolidateArgs (queryDef, args) {
  // return undefined if args fails to include all required parameters;
  // otherwise return a single object containing all supplied arguments (both required and optional)
  const argCount = args.length
  const paramObj = {}
  let argNum = 0
  let reqCount = 0
  let missingReq = false
  while (argNum < argCount && argNum <= reqCount) {
    const arg = args[argNum]
    const argValue = arg && arg.valueOf()
    const param = queryDef.params[argNum]
    if (param.req) { // required parameter
      reqCount++
      if (
        argValue
        || (param.type === 'text' && argValue === '')
        || (param.type === 'number' && argValue === 0)
        || (param.type === 'true/false' && typeof argValue !== 'undefined')
      ) {
        paramObj[param.name] = argValue
      } else {
        missingReq = true
      }
    } else if (!missingReq) { // optional parameters in one object
      if (argValue && typeof argValue === 'object') {
        // TODO: for security, filter out any unkonwn optional parameters
        return reqCount ? Object.assign(argValue, paramObj) : argValue
      }
    }
    argNum++
  }
  return missingReq ? undefined : paramObj
}

class DataManager {
  constructor (dataContext) {
    debug.assert(dataContext.name === GLOBALS)
    this.context = dataContext
    this.needDataAsyncFunc = undefined
    this.queryCache = {}
    this.typedObjCache = {}
    this.dataPromise = Promise.resolve()
    // this.newData = false
    // set up query delegates
    this.getStaticData = this.getStaticData.bind(this)
    this.delegates = {}
    const thisRef = this
    for (const queryDef of dataContext.typeDef.functions.filter(f => f.hasDelegate)) {
      this.delegates[queryDef.name] = function () { // can't be arrow function because we need "arguments"
        return thisRef.getStaticData(queryDef, ConsolidateArgs(queryDef, arguments))
      }
    }
  }

  // cacheValue (typeName, key, value) {
  //   if (value) {
  //     if (value instanceof ObjectContext) {
  //       value = DataManager.extractPojo(value)
  //     }
  //     const objCache = this.typedObjCache[typeName] || (this.typedObjCache[typeName] = {})
  //     objCache[key] = value
  //   }
  // }

  // static extractPojo (value) {
  //   // todo: this is incomplete; finish if it's needed/called!
  //   if (value) {
  //     if (Array.isArray(value)) {
  //       value = value.map(v => DataManager.extractPojo(v))
  //     } else if (value.__value) {
  //       value = value.__value
  //     } else if (value instanceof ObjectContext) {
  //       value = value.scope.value
  //     }
  //   }
  //   return value
  // }

  hasCachedValues () {
    if (this.typedObjCache) {
      const types = Object.keys(this.typedObjCache)
      if (types.length > 0) {
        return types.some(name => Object.keys(this.typedObjCache[name]).length > 0)
      }
    }
    return false
  }

  hasCachedValue (typeName, key) {
    const objCache = this.typedObjCache[typeName]
    return !!objCache && !!objCache[key]
  }

  hasAnyCachedValue (key) {
    // return true if the given key appears in ANY typed object cache (yes this seems a bit sketchy/fuzzy)
    return Object.values(this.typedObjCache).some(toc => key in toc)
  }

  getCachedValue (typeName, key, nativeDates = true, recursive = true, recurseProtect = {}) {
    if (Array.isArray(key)) {
      return key.map(k => this.getCachedValue(typeName, k, nativeDates, recursive, recurseProtect))
    }
    const objCache = this.typedObjCache[typeName]
    const value = objCache && objCache[key]
    return this.expandCachedObject(typeName, value, nativeDates, recursive, recurseProtect)
  }

  expandCachedObject (typeName, value, nativeDates = true, recursive = true, recurseProtect = {}) {
    if (value && typeof value === 'object') {
      const typeDef = this.context.typeDefs[typeName]
      if (typeDef) {
        const newValue = {}
        for (const [propName, propVal] of Object.entries(value)) {
          let cachedObj = undefined
          const varDef = (typeDef.variables || typeDef.properties).find(def => def.name === propName)
          if (varDef) {
            if ((varDef.type === 'object' || varDef.type === 'selection') && !varDef.typeName.startsWith('_')) {
              const dm = this
              const keyToObj = (keyVal => (keyVal && typeof keyVal !== 'object'
                ? (!recursive && varDef.type !== 'object') || ((typeName + '/' + keyVal) in recurseProtect)
                  ? keyVal
                  : dm.getCachedValue(varDef.typeName, keyVal, nativeDates, recursive, {
                    [typeName + '/' + keyVal]: true,
                    ...recurseProtect
                  })
                : keyVal && typeof keyVal === 'object' && varDef.type === 'object' // object variable without key value
                  ? dm.expandCachedObject(varDef.typeName, keyVal, nativeDates, recursive, {
                    [typeName + '/__nokey__']: true,
                    ...recurseProtect
                  })
                  : undefined
              ))
              if (varDef.isList && Array.isArray(propVal)) {
                cachedObj = propVal.map(keyToObj)
              } else {
                cachedObj = keyToObj(propVal)
              }
            }
            newValue[propName] = cachedObj || CoerceScalar(propVal, varDef, nativeDates)
          }
        }
        return newValue
      }
      return value
    }
    return undefined
  }

  /**
   * Caches an item (POJO value) selected from a query.  The cached item will be
   *   - kept around to detect if or when the selected query record changes, e.g., at some future re-run of the app
   *     (to prompt the user that the original has changed, and ask whether they want to update the linked answers)
   *   - AND PROBABLY (for queries with writeback) to detect when the "linked" answer has been modified, i.e.,
   *     whether to prompt the user whether or not to save changes back to the selected item in the data source.
   * Note that cached object values may correspond to object variables, selection variables, or both. These values
   * themselves are never mutated -- they are only meant to be a static representation of a value defined somewhere
   * else!
   * @param {string} typeName - Name of the model of which value is an instance
   * @param {string|number|array} key - Key under which value will be cached
   * @param {*} value - a POJO intended to be an instance of typeName
   */
  setValueForKey (typeName, key, value) {
    if (Array.isArray(key) && Array.isArray(value)) {
      key.forEach((k, idx) => this.setValueForKey(typeName, k, value[idx]))
      return
    }
    if (value && typeof value === 'object') { // don't cache garbage/buggy/dummy values
      if (isPrimitiveWrapper(value)) {
        return // nothing to do
      }
      if ((value instanceof ObjectContext) || (value instanceof Scope)) {
        throw new Error('caching unexpected value')
      }
      const objCache = this.typedObjCache[typeName] || (this.typedObjCache[typeName] = {})
      const valCopy = { ...value } // copy object so we never modify the original
      const typeDef = this.context.typeDefs[typeName]
      if (typeDef) {
        for (const [propName, propVal] of Object.entries(valCopy)) {
          if (propVal) {
            const varDef = (typeDef.variables || typeDef.properties).find(def => def.name === propName)
            if (varDef) {
              if (varDef.type === 'object' || varDef.type === 'selection') {
                const vTypeDef = this.context.typeDefs[varDef.typeName]
                if (vTypeDef) {
                  const ignoreKey = vTypeDef.type === 'object' && vTypeDef.keyType === 'none'
                  const keyName = (varDef.type === 'selection' && varDef.optionKeyName)
                    || vTypeDef.key || 'id$'
                  const dm = this
                  const objToKey = (objVal => {
                    if (objVal && typeof objVal === 'object' && !ignoreKey) {
                      if (keyName in objVal) {
                        const keyVal = objVal[keyName]
                        dm.setValueForKey(vTypeDef.name, keyVal, objVal)
                        return keyVal
                      } else {
                        console.warn(`Unkeyed object (no ${keyName}) in cache`)
                      }
                    }
                    return objVal
                  })
                  if (varDef.isList && Array.isArray(propVal)) {
                    valCopy[propName] = propVal.map(objToKey)
                  } else {
                    valCopy[propName] = objToKey(propVal)
                  }
                } else {
                  // error: there is an object or selection variable in the app definition, that uses/refers to
                  // a model that is NOT in the app definition. This should not happen... if/when it does, it
                  // indicates a bug in the app compiler.
                  console.error("AppDefinitionError: model '%s' not found, but used by %s variable '%s' of model '%s'",
                    varDef.typeName, varDef.type, varDef.name, typeDef.name)
                }
              } else {
                // ensure propVal is expected type
                let coercedVal = CoerceValue(propVal, varDef, false)
                valCopy[propName] = coercedVal
              }
            }
          }
        }
      }
      if (objCache[key]) { // already present, so update
        Object.assign(objCache[key], valCopy)
      } else {
        objCache[key] = valCopy
      }
    }
  }

  /**
   * Recursively update a cached, typed object. Works on scalar values and (internally/recursively) lists.
   * Compares value to an already-cached value, and updates the internal cached value if necessary.
   * 
   * @param {string} typeName the name of the model describing the object(s) to be updated/cached
   * @param {object} fields a 'field mask' object indicating which fields were requested when value was retrieved
   * @param {object|array|undef} value a POJO (or an array of POJOs) that represent(s an) instance(s) of typeName
   *                                   that may be more up-to-date than what is already in the cache
   * @param {object|array|undef} noKeyCached a POJO (or an array of POJOs) representing unkeyed child object(s)
   *                                         that are (already) cached as part of a parent object
   * 
   * @returns {object|array} the recursively expanded, updated value representing what is now cached, BUT with
   *                         two additional (intended to be temporary) properties:
   *                         __keyVal__, which is the key (or array of keys) of the returned object (or array), and
   *                         __mutated__, which will be true if any requested fields of the cached value were changed
   */
   updateValue (typeName, fields, value, noKeyCached = undefined) {
    const dm = this
    if (Array.isArray(value)) {
      const keyArray = []
      let hasKeys = false, mutated = false, nkc
      if (noKeyCached && Array.isArray(noKeyCached)) {
        if (noKeyCached.length === value.length) {
          nkc = noKeyCached
        } else { // new array and cached array differ in length, therefore new array was mutated!
          mutated = true
        }
      }
      const returnArray = value.map((v, idx) => dm.updateValue(typeName, fields, v, nkc && nkc[idx]))
      returnArray.forEach((val, idx) => {
        if (val && typeof val === 'object') {
          if ('__keyVal__' in val) {
            hasKeys = true
            keyArray[idx] = val.__keyVal__
            delete val.__keyVal__
          }
          if ('__mutated__' in val) {
            mutated = true
            delete val.__mutated__
          }
        }
      })
      if (hasKeys) {
        returnArray.__keyVal__ = keyArray
      }
      if (mutated) {
        returnArray.__mutated__ = mutated
      }
      return returnArray
    }
    if (value && typeof value === 'object') {
      const typeDef = this.context.typeDefs[typeName]
      if (typeDef) {
        const specificFields = fields && typeof fields === 'object' && Object.keys(fields).length > 0
        const notFound = specificFields ? { ...fields } : {}
        const keyName = typeDef.keyType === 'none' ? '' : (typeDef.key || 'id$')
        let keyVal
        const keyVal_orig = keyName && value[keyName]
        if (keyVal_orig && typeof keyVal_orig === 'object') {
          const keyPath = this.context.getKeyPath(false, typeDef)
          keyVal = keyPath && _.get(value, keyPath)
        } else {
          keyVal = keyVal_orig
        }
        if (keyName && !keyVal) {
          console.warn(`Unkeyed child object (no ${keyName}) returned by query`)
          if (!noKeyCached) {
            noKeyCached = value
          }
        }
        if (keyVal || !keyName || noKeyCached) {
          const objCache = keyName
            ? (this.typedObjCache[typeName] || (this.typedObjCache[typeName] = {}))
            : undefined
          const cachedValue = objCache && keyVal
            ? (objCache[keyVal] || (objCache[keyVal] = {}))
            : ((noKeyCached && typeof noKeyCached === 'object') ? { ...noKeyCached } : {})
          if (!specificFields && cachedValue) {
            Object.keys(cachedValue).forEach(k => notFound[k] = 1)
          }
          const returnValue = {}
          let mutated = false
          // for each property in the new value...
          for (const [propName, propVal] of Object.entries(value)) {
            delete notFound[propName]
            const varDef = (typeDef.variables || typeDef.properties).find(def => def.name === propName)
            // merge propVal (recursively) with whatever may already be cached
            const propFields = (fields && typeof fields === 'object') ? fields[propName] : fields
            const updatedProp = dm.updateValue(varDef && varDef.typeName, propFields, propVal, cachedValue[propName])
            // update cached property
            if (updatedProp && typeof updatedProp === 'object') {
              if ('__keyVal__' in updatedProp) {
                cachedValue[propName] = updatedProp.__keyVal__
                delete updatedProp.__keyVal__
              } else {
                cachedValue[propName] = updatedProp
              }
              if ('__mutated__' in updatedProp) {
                if (propFields || !specificFields) { // only note mutations of requested fields
                  mutated = true
                }
                delete updatedProp.__mutated__
              }
            } else { // updatedProp is not an object or array -- still need to update its cached value!
              if (!HasMatchingValue(cachedValue[propName], updatedProp)) {
                if (propFields || !specificFields) { // only note mutations in requested fields
                  mutated = true
                }
                cachedValue[propName] = updatedProp
              }
            }
            // return only requested properties
            if (propFields || !specificFields) {
              returnValue[propName] = updatedProp
            }
          }
          // then, for each REQUESTED (or, if nothing requested, then FORMERLY CACHED) field that was NOT in value...
          for (const propName of Object.keys(notFound)) {
            if (cachedValue) { // delete the cached property that appears no longer to be current
              if (propName in cachedValue) {
                if (cachedValue[propName]) {
                  mutated = true
                }
                delete cachedValue[propName]
              }
            }
          }
          // lastly, temporarily add the key value to the returned value (removed by caller)
          returnValue.__keyVal__ = keyVal || cachedValue // cachedValue for objects without keys
          // AND temporarily add the mutated flag to the returned value (also removed by caller)
          if (mutated) {
            returnValue.__mutated__ = mutated
          }
          return returnValue
        } else {
          console.error(`Cannot update cached ${typeName}: no ${keyName} in ${JSON.stringify(value)}`)
        }
      } else if (!typeDef) {
        if (_.isEqual(value, noKeyCached)) {
          // value being updated is the same as what we already have, so ignore it
        } else {
          console.log(`Invalid type name '${typeName}' for substantial value change`)
        }
      }
    }
    // otherwise
    return value
  }

  fetchDataPromise (queryDef, args) {
    // this.newData = true
    return (this.dataPromise
        .then(() => this.needDataAsyncFunc(queryDef, args))
        .then(data => {
          if (data) {
            this.setStaticData(queryDef, data, args)
          }
          // this.newData = false
        })
    )
  }

  getStaticData (queryDef, args) {
    // must return an array of already-cached values synchronously, but on client, can also initiate fetch of new values
    if (!args) return [] // args is undefined if the query required arguments that were not supplied
    const queryCacheKey = GetQueryCacheName(queryDef.name, args)
    if (this.needDataAsyncFunc && !(queryCacheKey in this.queryCache)) {
      this.queryCache[queryCacheKey] = false // will be replaced with ArrayContext when setStaticData is called below
      // but the false is to ensure the same query is not issued multiple times before it returns
      this.dataPromise = this.fetchDataPromise(queryDef, args)
        .catch(reason => {
          console.log(`promise failed (to do: something better than console.log!): ${reason}`)
          this.dataPromise = Promise.resolve()
          // this.newData = false
        })
    }
    // the above will have begun the process of fetching fresh records for the query
    // in the meantime, return what is already cached (if anything)
    const arrayContext = this.queryCache[queryCacheKey]
    return (arrayContext && arrayContext.scope && arrayContext.scope.proxy) || []
  }

  setStaticData (queryDef, data, args, timestamp = undefined) {
    if (!timestamp) {
      timestamp = Date.now()
    }
    console.log(`Setting ${data.length} records from query`)
    // use the data and the query definition to create ArrayContext, then initSource + initVirtuals + initScope
    const context = this.context
    // DEBUG: make sure things look as expected (part 1)
    if (data.length > 0) {
      var typeDef = context.typeDefs[queryDef.typeName]
      var key = typeDef.key || 'id$'
      var keyType = typeDef.keyType || 'id'
      debug.assert(key in data[0])
      debug.assert(keyType) // should be set; for clio we expect 'number'
      if (queryDef.typeName.startsWith('clio:')) {
        debug.assert(key === 'id')
        debug.assert(keyType === 'number')
        debug.assert(typeof data[0][key] === 'number')
      }
    }
    const queryCacheKey = GetQueryCacheName(queryDef.name, args)
    const childState = context._createArrayContext(queryDef.name, queryDef.typeName, queryDef.keyName, true)
    childState.initSource(data, MODE_INIT)
    // DEBUG: make sure things look as expected (part 2)
    if (data.length > 0) {
      debug.assert(childState.typeDef === typeDef)
      debug.assert(childState.source.length === data.length)
      debug.assert(typeof childState.source[0] === 'object')
      debug.assert(!isPrimitiveWrapper(childState.source[0]))
      if (key !== 'id$') {
        debug.assert('id$' in childState.source[0])
        debug.assert(typeof childState.source[0].id$ === 'string')
        debug.assert(childState.source[0][key].toString() === childState.source[0].id$)
      }
      debug.assert(!childState.scope)
    }
    childState.initVirtuals()
    childState.initScope() // TODO: I don't think this is right.  Does it cache data where it's supposed to be?
    if (data) {
      childState.initSelections()
    }
    // DEBUG: make sure things look as expected, part 3
    if (data.length > 0) {
      debug.assert(!childState.source)
      debug.assert(childState.scope)
      debug.assert(childState.scope.value[0])
      debug.assert(!isPrimitiveWrapper(childState.scope.value[0]))
      if (typeDef.key !== 'id$') {
        debug.assert('id$' in childState.scope.value[0])
        debug.assert(typeof childState.scope.value[0].id$ === 'string')
        debug.assert(childState.scope.value[0][key].toString() === childState.scope.value[0].id$)
      }
    }
    // cache individual object values?
    childState.timestamp = timestamp
    this.queryCache[queryCacheKey] = childState
    if (context.staticDataRefreshed) { // !context.constructor.name.endsWith('Context')
      context.staticDataRefreshed()
    }
  }

  // selectObjects (queryDef, idArray) {
  //   const data = selected.data
  //   // async fetch entire selected option (rather than just minimal fields)
  //   const itemId = data && data[optionKeyName]
  //   if (itemId) {
  //     const nextValue = await FetchItem(query, itemId, optionKeyName, context, context.instanceId + '.' + name)
  //     if (nextValue) {
  //       onChange(nextValue)
  //       // const sel = context.getPushedContext(nextValue, query.typeName)
  //       // onChange(sel.scope.value)
  //     } else {
  //       console.error('Query returned invalid object after selection')
  //     }
  //   }
  // }

  extractStaticData (pojo) {
    // called when initially loading a new answer set into the runtime --
    // it checks for static cached query results and siphons those off into their own place
    if (('__cache__' in pojo) || ('__static__' in pojo)) {
      const { __static__, __cache__, ...otherData } = pojo

      // enumerate over staticData; for each
      if (__static__ && typeof __static__ === 'object') {
        for (let [key, thisData] of Object.entries(__static__)) {
          let timestamp
          // take the property key and look for a matching query definition
          const { qName, args } = ParseQueryCacheName(key)
          const queryDef = this.context.typeDef.functions && this.context.typeDef.functions.find(f => f.name === qName)
          // check if it has a fetched stamp; if so, use it; if not, assume it's an array
          if (thisData.timestamp) {
            timestamp = thisData.timestamp
            thisData = thisData.data
          }
          if (queryDef) {
            this.setStaticData(queryDef, thisData, args, timestamp)
          }
        }
      }
      // enumerate over cached objects
      if (__cache__ && typeof __cache__ === 'object') {
        for (const [typeName, typeVals] of Object.entries(__cache__)) {
          for (const [key, value] of Object.entries(typeVals)) {
            this.setValueForKey(typeName, key, value)
          }
        }
      }
      pojo = otherData
    }
    return pojo
  }

  injectStaticData (pojo) {
    var hasStatic = false
    var result = {}
    for (const [cacheKey, queryContext] of Object.entries(this.queryCache)) {
      const queryDef = this.context.typeDef.functions.find(f => f.name === queryContext.name)
      if (queryDef.generalUse) {
        hasStatic = true
        result[cacheKey] = queryContext.cleanArray()
      }
    }
    if (hasStatic) {
      pojo.__static__ = result
    }
    if (this.hasCachedValues()) {
      pojo.__cache__ = this.typedObjCache
    }
  }
}

/**
 * An IndirectTemplate is a marker for a sub-template that must be assembled separately, and then
 * once assembled, the resulting sub-document will be inserted into a parent document being assembled.
 *
 * This code also takes care of converting the result of such an Indirect assembly to the same format as the parent
 * assembly prior to it being inserted:  Markdown to DOCX (or vice versa)
 *
 * Order:
 * To begin the DOCX assembly process, an XML file is produced that consists of the results of every expression
 * referred to in the DOCX template.  Each of these expression results will be a string.  The result will also
 * include a collection of zero or more "indirects".  If this collection is empty, the XML produced is sufficient
 * to produce the final document, so this is done.  But if OTOH the indirects dictionary contains any items,
 * each of those represents an IndirectTemplate that must be assembled and (if necessary) converted into a format
 * compatible with the parent assembly. When all the indirects are complete, the parent document and each
 * indirect document must then be composed into a single document. And since assembly of an indirect can itself
 * result in further indirects, this process is recursive.
 *
 * For text assembly, all insertions are resolved synchronously as they are encountered -- the indirects dictionary
 * is not used.
 */
class IndirectTemplate {
  constructor (templateDef, typeDef) {
    this.templateDef = templateDef
    this.typeDef = typeDef
    if (templateDef.type === 'file' || templateDef.type === 'text') {
      this.func = compileText(templateDef.text)
    }
    this.get = this.get.bind(this)
    this.get.logic = true
  }

  get (scope) {
    const indirect = new IndirectVirtual(
      {
        templateDef: this.templateDef,
        name: this.templateDef.name,
        type: this.templateDef.type,
        typeDef: this.typeDef,
        typeName: this.typeDef.name,
      },
      scope,
      (this.templateDef.type === 'docx' ? 'docx' : this.templateDef.contentType) || 'text',
    )
    if (indirect.type === 'file' || indirect.type === 'text') {
      indirect.toString = () => {
        const result = this.func(scope.scopeProxy, scope.proxy)
        return result && result.toString()
      }
    }
    return indirect
  }
}
IndirectTemplate.prototype.get.logic = true

function objHasProp (object, propName) {
  return Object.prototype.hasOwnProperty.call(object, propName)
}
// const isDef = val => val !== null && typeof val !== 'undefined'

function GetQueryCacheName (queryName, args) {
  let result = queryName
  if (args) {
    if (typeof args === 'object') {
      args = JSON.stringify(args)
    }
    result += ':' + args
  }
  return result
}

function ParseQueryCacheName (key) {
  const argpos = key.indexOf(':')
  const qName = (argpos >= 0) ? key.substring(0, argpos) : key
  const args = (argpos >= 0) ? JSON.parse(key.substring(argpos + 1)) : undefined
  return { qName, args }
}

function CoerceValue (inputValue, varDef, nativeDates) {
  if (varDef.isList) {
    return CoerceArray(inputValue, varDef, nativeDates)
  } else {
    return CoerceScalar(inputValue, varDef, nativeDates)
  }
}

function CoerceScalar (inputValue, varDef, nativeDates) {
  if (Array.isArray(inputValue)) {
    // we have an array?? Error? Nah, just toss everything but the first value, I guess...
    // (That is... this an authoring error, but we currently choose to let it slide.)
    console.log('Warning: array found where single value expected')
    inputValue = inputValue[0]
  }
  if (varDef.type === 'object' || varDef.type === 'selection') {
    // un-proxy (whether normal objects or wrapped primitives)
    return (inputValue && (inputValue.__value || inputValue)) || null
  }
  let type = typeof inputValue
  let tempValue
  switch (varDef.type) {
    case 'text':
      return (type === 'string') ? inputValue : inputValue ? String(inputValue) : null
    case 'number':
      tempValue = (type === 'number') ? inputValue : numeral(inputValue).value()
      return Number.isFinite(tempValue) ? tempValue : null
    // case 'bigint':
    //   return (type === 'bigint') ? inputValue : BigInt(inputValue)
    case 'date':
      if (!nativeDates && type === 'string' && inputValue.length === 10) {
        return inputValue
      }
      tempValue = (type === 'object' && inputValue && inputValue.constructor === Date)
        ? inputValue
        : parseISO(inputValue)
      if (!Number.isFinite(tempValue.getTime())) {
        return null
      }
      return nativeDates ? tempValue : format(tempValue, 'yyyy-MM-dd')
    default:
      // else for boolean, object, or anything else: no special action
      return inputValue
  }
}

/* from _cleanScalarValue, the sort-of inverse of the above:
      if (typeof outgoingValue === 'object' && outgoingValue !== null) {
        if (isPrimitiveWrapper(outgoingValue)) {
          return outgoingValue.valueOf()
        } else if (outgoingValue instanceof Date) {
          // not possible yet, but in the future if a table can have a date column, and the date column can be key...
          return format(outgoingValue, 'yyyy-MM-dd')
        } else if (varDef.optionKeyName in outgoingValue) {
          return outgoingValue[varDef.optionKeyName]
        } else {
          return outgoingValue.id$
        }
      } else {
        return outgoingValue
      }
*/

function CoerceArray (inputArray, varDef, nativeDates) {
  if (!Array.isArray(inputArray)) {
    // we have only a single item?? Error? Nah, just inject that item in a new array, I guess...
    console.log('Warning: single value found where array expected')
    inputArray = [inputArray]
  }
  // map to an array of appropriately-typed values
  return inputArray.map((element, index) => CoerceScalar(element, varDef, nativeDates))
}

function LookupOption (options, key) {
  let foundOption
  // options is typically expected to be an array of objects
  // however, if it IS an array of strings (because e.g. something maybe didn't initialize correctly,
  // although I'm not sure when that might be the case) then still do the lookup so the answer is not lost!
  const isPrimitive = options.length && (isPrimitiveWrapper(options[0]) || typeof options[0] === 'string')
  if (isPrimitive) {
    if (isPrimitiveWrapper(key)) {
      key = key.valueOf()
    }
    foundOption = options.find(option => option == key) // eslint-disable-line eqeqeq
  } else {
    foundOption = options.find(option => option.id$ === key)
  }
  if (typeof foundOption === 'undefined') {
    foundOption = null
  }
  return foundOption
}

function wrapPrimitive (value, sourceObj = undefined, keyName = undefined) {
  let result
  switch (typeof value) {
    case 'string':
      // eslint-disable-next-line no-new-wrappers
      result = new String(value)
      break
    case 'number':
      // eslint-disable-next-line no-new-wrappers
      result = new Number(value)
      break
    // case 'bigint':
    //   // eslint-disable-next-line no-new-wrappers
    //   result = new BigInt(value)
    //   break
    case 'boolean':
      // eslint-disable-next-line no-new-wrappers
      result = new Boolean(value)
      break
    default:
      throw new Error('unexpected value type')
  }
  if (sourceObj) {
    for (const [k, v] of Object.entries(sourceObj)) {
      Object.defineProperty(result, k, { value: v })
      if (k === keyName) {
        Object.defineProperty(result, 'id$', { value: v.toString() })
      }
    }
  }
  return result
}

function isPrimitiveWrapper (obj) {
  const value = (obj && obj.__value) || obj // look inside proxies
  if (typeof value === 'object') {
    const constructor = value && value.constructor
    return constructor && ['String', 'Number', 'Boolean'/* , 'BigInt' */].includes(constructor.name)
  }
  return false
}

function getFormulaExpression (formulaDef) {
  if (!formulaDef.isList) {
    return formulaDef.expression
  } // else isList
  const exprArray = JSON.parse(formulaDef.expression)
  if (!Array.isArray(exprArray)) {
    throw new Error('Error: unexpected definition for list formula')
  }
  if (exprArray.length > 0)
    return '[].concat((' + exprArray.join('),(') + '))|filter:this'
  // else
  return '[]'
}

function GetCachedOrFallbackArray (keyVal, fallback, dataMgr, varDef, recursive, recurseProtect) {
  debug.assert(varDef.isList)
  if (!Array.isArray(keyVal)) {
    keyVal = (keyVal !== null && keyVal !== undefined) ? [keyVal] : []
  }
  const typeDefs = dataMgr.context.typeDefs
  let itemVal = keyVal.find(Boolean) // get one individual item value (just to check its type)
  const iv = GetSelectionKeyValue(itemVal, varDef, typeDefs) // get key value, incl. nested keys
  if (itemVal !== iv) {
    fallback = keyVal
    itemVal = iv
    keyVal = keyVal.map(iVal => GetSelectionKeyValue(iVal, varDef, typeDefs))
  }
  if (typeof itemVal === 'string' || typeof itemVal === 'number') {
    const cachedVal = keyVal.map(k => {
      let mappedVal
      const fullName = varDef.typeName + '/' + k
      if (!recurseProtect[fullName]) {
        mappedVal = dataMgr.getCachedValue(varDef.typeName, k, true, recursive)
        if (mappedVal && typeof mappedVal === 'object') {
          recurseProtect[fullName] = true
        }
      } else {
        mappedVal = k
      }
      return (mappedVal && typeof mappedVal === 'object' && varDef.optionKeyName && (varDef.optionKeyName in mappedVal))
        ? mappedVal
        : (mappedVal && varDef.optionKeyName)
          ? { [varDef.optionKeyName]: mappedVal }
          : mappedVal
    })
    if (cachedVal && Array.isArray(cachedVal)) {
      const cachedItem = cachedVal.find(Boolean)
      if (cachedItem && typeof cachedItem === 'object') {
        return cachedVal
      }
    }
  }
  if (fallback) {
    if (!Array.isArray(fallback)) {
      fallback = [fallback]
    }
    if (fallback.length === keyVal.length &&
      fallback.every((item, index) => (
        (item && item[varDef.optionKeyName] === keyVal[index]) || (item === keyVal[index])
      ))
    ) { // legacy case: fallback passed in only when processing legacy answer data containing +'_$' answers
      return fallback
    }
  }
  if (typeof itemVal === 'string' || typeof itemVal === 'number') {
    // otherwise return a dummy object array with the requested keys, so as not to lose the selection answers
    return keyVal.map(kv => kv ? { [varDef.optionKeyName]: kv } : kv )
  }
  // else default/fall-back behavior
  return keyVal
}

function GetSelectionKeyValue (keyVal, varDef, typeDefs) {
  const isOptionKey = v => v.name === varDef.optionKeyName
  while (keyVal && typeof keyVal === 'object' && varDef.optionKeyName && (varDef.optionKeyName in keyVal)) {
    keyVal = keyVal[varDef.optionKeyName]
    if (typeof keyVal === 'object') { // keep recursing to get nested key value
      const typeDef = typeDefs[varDef.typeName]
      varDef = typeDef && typeDef.variables.find(isOptionKey)
    }
  }
  return keyVal
}

function GetCachedOrFallbackObj (keyVal, fallback, dataMgr, varDef, recursive, recurseProtect) {
  debug.assert(!varDef.isList)
  if (Array.isArray(keyVal)) { // get first meaningful value out of array
    keyVal = keyVal.find(Boolean) || (keyVal.length > 0 ? keyVal[0] : undefined)
  }
  const keyValWasObject = (keyVal && typeof keyVal === 'object')
  const kv = GetSelectionKeyValue(keyVal, varDef, dataMgr.context.typeDefs) // get key value, incl. nested keys
  if (keyVal !== kv) {
    fallback = keyVal
    keyVal = kv
  }
  if (typeof keyVal === 'string' || typeof keyVal === 'number') {
    const fullName = varDef.typeName + '/' + keyVal
    if (!recurseProtect[fullName]) {
      const cachedVal = dataMgr.getCachedValue(varDef.typeName, keyVal, true, recursive)
      if (cachedVal && typeof cachedVal === 'object') {
        recurseProtect[fullName] = true
        return cachedVal
      }
    }
    if (fallback) {
      if (Array.isArray(fallback)) { // get first meaningful item out of array
        fallback = fallback.find(Boolean) || fallback.length > 0 ? fallback[0] : undefined
      }
      if (fallback && (keyValWasObject || fallback[varDef.optionKeyName] === keyVal)) {
        // either keyValWasObject (means, we are probably in a "get temporary pushed context" scenario)
        // or the legacy case: fallback was passed in when processing legacy answer data containing +'_$' answers
        return fallback
      }
    }
    // otherwise return a dummy object with the requested key, so as not to lose the selection answer
    if (keyVal) {
      return { [varDef.optionKeyName]: keyVal }
    }
  }
  // else default/fall-back behavior
  return keyVal
}

function IsObjectVar (varDef) {
  if (varDef.type === 'object') return true
  if (varDef.type === 'selection' && varDef.cacheOpts) return true
  return false
}

function HasValue (a) {
  if (a === undefined || a === null || a === '') {
    return false
  }
  if (typeof a === 'object') {
    return Object.keys(a).length > 0
  }
  return true
}

function HasMatchingValue (a, b) {
  if (HasValue(a)) {
    if (HasValue(b)) {
      return a === b
    }
    return false
  }
  if (HasValue(b)) {
    return false
  }
  return true
}

// function arraysMatch (arr1, arr2) {
//   if (!Array.isArray(arr1)) throw new Error('arraysMatch called on non-array')
//   if (!Array.isArray(arr2)) {
//     debug.assert(false, `Array/non-array mismatch`, true, true)
//     return false
//   }
//   if (arr1.length !== arr2.length) {
//     debug.assert(false, 'Array length mismatch', true, true)
//     return false
//   }
//   // else
//   return true
// }

// function objectKeysMatch (keyOrObj1, obj2, keyName) {
//   if (Array.isArray(keyOrObj1)) throw new Error('objectKeysMatch called on array')
//   if (Array.isArray(obj2)) {
//     debug.assert(false, `Array/non-array mismatch`, true, true)
//     return false
//   }
//   const key1 = (typeof keyOrObj1 === 'object') ? keyOrObj1[keyName] : keyOrObj1
//   if (keyName in obj2) {
//     if (obj2[keyName] !== key1) {
//       debug.assert(false, 'Object key mismatch', true, true)
//       return false
//     }
//   }
//   return true
//   // if these asserts fail, it may be because of changes from 1/6/2022 where we abolished _id in favor of id$?
//   // else the selection refers to a record that may not (yet?) be part of the option source in question
// }

function createTypedObject (sourceObj, keyName, virtualDefs, varDefs, typeDefs,
  retainId$, forceNew, forceOID = undefined
) {
  const prototype = virtualDefs || Object.prototype
  if (sourceObj) {
    if (typeof sourceObj !== 'object') {
      if (keyName === 'id$') {
        sourceObj = { id$: retainId$ ? sourceObj.toString() : (forceOID || objectId()) }
        if (!retainId$) {
          sourceObj._link = sourceObj
        }
      } else { // keyName !== 'id$'
        sourceObj = { id$: sourceObj.toString() }
        if (keyName) {
          sourceObj[keyName] = sourceObj
        }
      }
    } else { // sourceObj && typeof sourceObj === 'object'
      if (sourceObj.id$) {
        if (!retainId$ && (!keyName || keyName === 'id$')) {
          sourceObj = assignTypedProps(Object.create(prototype), sourceObj, varDefs, typeDefs,
            retainId$, forceNew, forceOID)
          sourceObj.id$ = forceOID || objectId()
          return sourceObj
        }
      } else { // !sourceObj.id$
        if (keyName && keyName !== 'id$' && sourceObj[keyName]) {
          sourceObj = assignTypedProps(Object.create(prototype), sourceObj, varDefs, typeDefs,
            retainId$, forceNew, forceOID)
          sourceObj.id$ = sourceObj[keyName].toString()
          return sourceObj
        }
      }
    }
  } else { // !sourceObj
    if (keyName === 'id$') { // should we maybe add: || !keyName here?  Would need testing.
      sourceObj = { id$: forceOID || objectId() }
    }
  }
  if (!forceNew && sourceObj && (Object.getPrototypeOf(sourceObj) === prototype)) {
    // sourceObj appears good to use as-is
    return sourceObj
  } // else
  // create a new object with the appropriate prototype
  return Object.assign(Object.create(prototype), sourceObj)
}

function assignTypedProps (toObj, fromObj, varDefs = undefined, typeDefs = undefined,
  retainId$ = undefined, forceNew = false, forceOID = undefined
) {
  if (!varDefs || !varDefs.length) {
    return Object.assign(toObj, fromObj)
  }
  if (fromObj !== undefined && fromObj !== null) {
    let propKey
    const matchingName = def => def.name === propKey
    for (propKey of Object.keys(fromObj)) {
      let propVal = fromObj[propKey]
      if (propVal) {
        const varDef = varDefs.find(matchingName)
        if (varDef) {
          if (IsObjectVar(varDef)) {
            const typeDef = typeDefs[varDef.typeName]
            if (varDef.isList) {
              if (!Array.isArray(propVal)) propVal = [propVal]
              propVal = propVal.map(elemVal => (elemVal && typeof elemVal === 'object')
                ? createTypedObject(elemVal, varDef.optionKeyName || typeDef.key || 'id$',
                  typeDef.virtuals, typeDef.variables, typeDefs, retainId$, forceNew, forceOID)
                : elemVal
              )
            } else {
              if (typeof propVal === 'object') {
                propVal = createTypedObject(propVal, varDef.optionKeyName || typeDef.key || 'id$',
                  typeDef.virtuals, typeDef.variables, typeDefs, retainId$, forceNew, forceOID)
              }
            }
          } else if (varDef.type === 'date') {
            if (typeof propVal === 'string') {
              propVal = parseISO(propVal)
            }
          }
        }
      }
      toObj[propKey] = propVal
    }
  }
  return toObj
}

function deleteMatchingProps (fromObj, matchObj) {
  if (fromObj && matchObj) {
    for (const key of Object.keys(fromObj)) {
      if (key in matchObj) {
        delete fromObj[key]
      }
    }
  }
}

function CopyNewProperties (destination, source) {
  if (!source) return
  for (const [propKey, sourceVal] of Object.entries(source)) {
    if (propKey in destination) {
      // destination object already has property with same name as source
      const destVal = destination[propKey]
      if (Array.isArray(destVal)) {
        if (Array.isArray(sourceVal)) {
          const destFirst = destVal[0]
          const sourceFirst = sourceVal[0]
          if (destVal.length || sourceVal.length) {
            const itemType = (destFirst && typeof destFirst) || typeof sourceFirst
            if (itemType === 'object') {
              if (destVal.length === sourceVal.length) {
                for (let i = 0; i < destVal.length; i++) {
                  const destItem = destVal[i]
                  if (typeof destItem === 'object' && destItem !== null) {
                    CopyNewProperties(destItem, sourceVal[i])
                  }
                }
              }// else { // commented out for when a new item is added to a list...
              //   throw new Error(`Mismatch in '${propKey}' list lengths`)
              // }
            }
          }
        }
      } else if (typeof destVal === 'object' && destVal !== null) {
        // mix/combine object values
        CopyNewProperties(destVal, sourceVal)
      }
      // otherwise do nothing -- the source value trumps whatever existed before
    } else {
      // the destination does not already have prop, so just assign it
      destination[propKey] = sourceVal
    }
  }
}

function InitAppType (t) {
  // interpret/reconstitute model-level flags
  if ('f' in t) {
    if (!('isExternal' in t) && flagIsSet(t.f, FLAG_EXTERNAL)) {
      t.isExternal = true
    }
  }
  // interpret/reconstitute variable-level flags
  for (const v of t.variables) {
    if ('f' in v) {
      if (!('isExternal' in v) && flagIsSet(v.f, FLAG_EXTERNAL)) {
        v.isExternal = true
      }
    }
  }
  return t
}

function InitAppState (appDef) {
  // build type dictionary
  const typeDict = {}
  for (const t of appDef.types) {
    typeDict[t.name] = InitAppType(t)
  }
  for (const t of globalTypes) {
    if (t.name !== GLOBAL_TYPENAME) {
      typeDict[t.name] = { ...t } // copy global type objects so as not to pollute them
    } else {
      const { properties, functions, ...globalDef } = t // the __global type needs copies of the props/funcs arrays
      globalDef.properties = [...properties] // so the dynamic addition of tables, TOPID,
      globalDef.functions = [...functions] // and queries (below) doesn't pollute the "original" object
      // (Note: '_' (TOPID) is more like a virtual, not just a regular property,
      // and at some point probably ought to be treated as such.)
      // (Queries without parameters could be treated as virtuals too, but since queries CAN have parameters,
      // I chose to treat them as functions instead.)
      typeDict[t.name] = globalDef
    }
  }
  // build dictionary of global constants (including tables)
  const constDict = {}
  const globalTypeDef = typeDict[GLOBAL_TYPENAME]
  for (const def of appDef.globals.filter(g => g.type === 'table' || g.type === 'variable')) {
    // data must contain "typed" object values -- but they get initialized/"enhanced" later
    constDict[def.name] = def.data
    globalTypeDef.properties.push({
      name: def.name,
      type: 'object',
      typeName: def.typeName,
      keyName: def.key,
      isList: def.type === 'table' || def.isList,
    })
  }
  for (const queryDef of appDef.globals.filter(g => g.type === 'query')) {
    const runtimeFuncDef = {
      name: queryDef.name,
      addon: true,
      type: 'object',
      typeName: queryDef.typeName,
      keyName: queryDef.key,
      provider: queryDef.provider,
      source: queryDef.sourceName,
      isList: true,
      label: queryDef.label ||
        (queryDef.provider === 'Knackly' ? queryDef.sourceName : queryDef.provider),
      writeBack: queryDef.writeBack || false,
      generalUse: queryDef.generalUse || false,
      hasDelegate: true, // query delegates are added to constDict later in initialization
    }
    if (Array.isArray(queryDef.params)) {
      runtimeFuncDef.params = queryDef.params // needed for parameter processing at runtime (consolidation)
      runtimeFuncDef.parameters = queryDef.params.map(pdef => pdef.type) // is this needed? not sure.
    }
    if (queryDef.fields) {
      runtimeFuncDef.fields = queryDef.fields
    }
    globalTypeDef.functions.push(runtimeFuncDef)
  }
  constDict['math'] = constDict['Math'] = math
  constDict['text'] = text
  constDict['number'] = number
  constDict['date'] = date
  constDict['finance'] = finance
  // constDict['isFinite'] = isFinite
  // constDict['isNaN'] = isNaN
  // constDict['parseInt'] = parseInt
  // constDict['parseFloat'] = parseFloat
  constDict['peek'] = peek
  constDict['_app'] = {
    Name: appDef.name,
    Label: appDef.label,
    Catalog: appDef.catid,
  }
  constDict['appLink'] = function (appName, userEmail, userName = undefined) {
    return '[link not available]'
  }
  // location (within the global context) where user data is stored:
  globalTypeDef.properties.push({
    name: USERDATA, // '_'
    type: 'object',
    typeName: appDef.mainType,
    keyName: 'id$',
    isList: false,
  })
  constDict[USERDATA] = null // this is the one property that WON'T be constant -- the app data
  return { typeDict, constDict }
}

export {
  ObjectContext,
  ArrayContext,
  CoercedExpression,
  IndirectTemplate,
  CoerceScalar,
  ConsolidateArgs,
  InitAppState,
  IsObjectVar,
  MODE_REPLACE,
  MODE_INIT,
  MODE_OVERLAY,
  MODE_OVERLAY_CHILD,
}
